/**
 * Unilar - Ficha Técnica
 * Parses product specs JSON and expands/collapses the table with fade overlay.
 */

class UnilarFichaTecnica extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  /** @type {HTMLElement | null} */
  #content = null;
  /** @type {HTMLElement | null} */
  #table = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');
  #boundBreakpointHandler = this.#syncState.bind(this);

  connectedCallback() {
    this.#toggle = this.querySelector('.ficha-tecnica__toggle');
    this.#content = this.querySelector('.ficha-tecnica__content');
    this.#table = this.querySelector('.ficha-tecnica__table');

    if (!this.#toggle || !this.#content || !this.#table) return;

    this.#renderSpecs();

    this.#toggle.addEventListener('click', this.#handleToggle.bind(this));
    this.#mobileMediaQuery.addEventListener('change', this.#boundBreakpointHandler);

    this.#resizeObserver = new ResizeObserver(() => this.#syncState());
    this.#resizeObserver.observe(this.#content);

    this.#syncState();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mobileMediaQuery.removeEventListener('change', this.#boundBreakpointHandler);
  }

  #renderSpecs() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    let specs;

    try {
      const raw = JSON.parse(script.textContent || 'null');
      specs = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return;
    }

    if (!Array.isArray(specs)) return;

    const fragment = document.createDocumentFragment();

    for (const item of specs) {
      if (!item || typeof item !== 'object') continue;

      const rawName = item.name ?? item.Name;
      const rawValue = item.value ?? item.Value;
      const name = typeof rawName === 'string' ? rawName.trim() : '';
      const value = rawValue == null ? '' : String(rawValue).trim();

      if (!name || !value) continue;

      const row = document.createElement('div');
      row.className = 'ficha-tecnica__row';
      row.setAttribute('role', 'row');

      const label = document.createElement('div');
      label.className = 'ficha-tecnica__label';
      label.setAttribute('role', 'rowheader');
      label.textContent = name;

      const cell = document.createElement('div');
      cell.className = 'ficha-tecnica__value';
      cell.setAttribute('role', 'cell');
      cell.textContent = value;

      row.append(label, cell);
      fragment.appendChild(row);
    }

    this.#table.replaceChildren(fragment);
  }

  #handleToggle() {
    const isExpanded = this.classList.toggle('is-expanded');
    this.#toggle.setAttribute('aria-expanded', String(isExpanded));
    this.#updateLabel(isExpanded);
  }

  #updateLabel(isExpanded) {
    const label = this.#toggle.querySelector('.ficha-tecnica__toggle-label');
    if (!label) return;

    label.textContent = isExpanded
      ? this.dataset.labelLess || 'Ver menos detalhes'
      : this.dataset.labelMore || 'Ver mais detalhes';
  }

  #getCollapsedHeight() {
    const styles = getComputedStyle(this);

    if (this.#mobileMediaQuery.matches) {
      const mobileHeight = parseInt(
        styles.getPropertyValue('--ficha-tecnica-collapsed-height-mobile'),
        10
      );

      if (mobileHeight) return mobileHeight;
    }

    return parseInt(styles.getPropertyValue('--ficha-tecnica-collapsed-height'), 10) || 280;
  }

  #syncState() {
    const collapsedHeight = this.#getCollapsedHeight();
    const needsToggle = this.#content.scrollHeight > collapsedHeight + 1;

    this.classList.toggle('is-not-collapsible', !needsToggle);

    if (needsToggle) {
      this.style.setProperty('--ficha-tecnica-expanded-height', `${this.#content.scrollHeight}px`);
    } else {
      this.classList.remove('is-expanded');
      this.#toggle.setAttribute('aria-expanded', 'false');
      this.#updateLabel(false);
    }
  }
}

if (!customElements.get('unilar-ficha-tecnica')) {
  customElements.define('unilar-ficha-tecnica', UnilarFichaTecnica);
}
